
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageTransition from '@/components/PageTransition';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { generateIndustryServiceSchema } from '@/utils/seoSchema';
import { updateMetaTags, getCanonicalUrl } from '@/utils/seoUtils';
import HeroSection from '@/components/social-media/HeroSection';
import WhyKIVideos from '@/components/social-media/WhyKIVideos';
import ProcessSteps from '@/components/social-media/ProcessSteps';
import ExampleGallery from '@/components/social-media/ExampleGallery';
import FAQSection from '@/components/social-media/FAQSection';
import CtaSection from '@/components/social-media/CtaSection';
import FloatingCTA from '@/components/social-media/FloatingCTA';

const TourismIndustry = () => {
  const { lang, city } = useParams();
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  
  useEffect(() => {
    // SEO meta tags setup based on language
    const titlesByLang = {
      de: city ? `KI-Videomarketing für Tourismus ${city} | Future Level` : "KI-Videomarketing für Tourismus | Future Level",
      en: city ? `AI Video Marketing for Tourism ${city} | Future Level` : "AI Video Marketing for Tourism | Future Level",
      fr: city ? `Marketing Vidéo IA pour Tourisme ${city} | Future Level` : "Marketing Vidéo IA pour Tourisme | Future Level",
      it: city ? `Marketing Video AI per Turismo ${city} | Future Level` : "Marketing Video AI per Turismo | Future Level"
    };
    
    const descriptionsByLang = {
      de: city ? 
        `Inspirierende KI-Videos für Hotels, Reiseveranstalter und Tourismusverbände in ${city}. Förderung von Reisezielen in der Schweiz durch innovative Videomarketinglösungen.` : 
        "Inspirierende KI-Videos für Hotels, Reiseveranstalter und Tourismusverbände. Förderung von Reisezielen in der Schweiz durch innovative Videomarketinglösungen.",
      en: city ?
        `Inspiring AI videos for hotels, tour operators, and tourism boards in ${city}. Promoting destinations in Switzerland through innovative video marketing solutions.` :
        "Inspiring AI videos for hotels, tour operators, and tourism boards. Promoting destinations in Switzerland through innovative video marketing solutions.",
      fr: city ?
        `Vidéos IA inspirantes pour hôtels, voyagistes et offices du tourisme à ${city}. Promotion des destinations en Suisse grâce à des solutions de marketing vidéo innovantes.` :
        "Vidéos IA inspirantes pour hôtels, voyagistes et offices du tourisme. Promotion des destinations en Suisse grâce à des solutions de marketing vidéo innovantes.",
      it: city ?
        `Video AI ispiranti per hotel, operatori turistici e enti del turismo a ${city}. Promozione delle destinazioni in Svizzera attraverso soluzioni di marketing video innovative.` :
        "Video AI ispiranti per hotel, operatori turistici e enti del turismo. Promozione delle destinazioni in Svizzera attraverso soluzioni di marketing video innovative."
    };
    
    const keywordsByLang = {
      de: city ? 
        `Tourismus KI-Videos ${city}, Hotel Marketingvideos ${city}, Reisevideos KI-erstellt ${city}, Destinationsmarketing Video, Schweizer Tourismus KI` :
        "Tourismus KI-Videos, Hotel Marketingvideos, Reisevideos KI-erstellt, Destinationsmarketing Video, Schweizer Tourismus KI",
      en: city ?
        `Tourism AI videos ${city}, hotel marketing videos ${city}, travel videos AI-created ${city}, destination marketing video, Swiss tourism AI` :
        "Tourism AI videos, hotel marketing videos, travel videos AI-created, destination marketing video, Swiss tourism AI",
      fr: city ?
        `Vidéos IA tourisme ${city}, vidéos marketing hôtels ${city}, vidéos voyage créées par IA ${city}, vidéo marketing de destination, tourisme suisse IA` :
        "Vidéos IA tourisme, vidéos marketing hôtels, vidéos voyage créées par IA, vidéo marketing de destination, tourisme suisse IA",
      it: city ?
        `Video AI turismo ${city}, video marketing hotel ${city}, video viaggio creati AI ${city}, video marketing destinazione, turismo svizzero AI` :
        "Video AI turismo, video marketing hotel, video viaggio creati AI, video marketing destinazione, turismo svizzero AI"
    };
    
    // Determine the canonical URL path based on language
    const pagePath = currentLanguage === 'de' ? '/leistungen/ki-videomarketing-tourismus' : 
                    currentLanguage === 'en' ? '/services/ai-video-marketing-tourism' : 
                    currentLanguage === 'fr' ? '/services/video-marketing-ia-tourisme' : 
                    '/servizi/marketing-video-ia-turismo';
                    
    const canonicalUrl = getCanonicalUrl(`${pagePath}${city ? `/${city.toLowerCase()}` : ''}`, currentLanguage);
    
    // Update meta tags for SEO
    updateMetaTags({
      title: titlesByLang[currentLanguage as keyof typeof titlesByLang],
      description: descriptionsByLang[currentLanguage as keyof typeof descriptionsByLang],
      keywords: keywordsByLang[currentLanguage as keyof typeof keywordsByLang],
      canonicalUrl: canonicalUrl,
      language: currentLanguage,
      ogType: "website"
    });
    
    // Add structured data with industry-specific schema
    generateIndustryServiceSchema(
      'tourism',
      titlesByLang[currentLanguage as keyof typeof titlesByLang],
      descriptionsByLang[currentLanguage as keyof typeof descriptionsByLang],
      "https://future-level.ch/opengraph-image-p98pqg.png",
      canonicalUrl,
      currentLanguage,
      city
    );
    
  }, [currentLanguage, city]);
  
  // Industry-specific content for tourism
  const getHeroContent = () => {
    const content = {
      de: {
        title: city ? `KI-Videomarketing für Tourismus in ${city}` : 'KI-Videomarketing für Tourismus',
        subtitle: 'Begeistern Sie potenzielle Gäste mit inspirierenden KI-Videos für Hotels, Destinationen und Reiseanbieter',
        description: 'Präsentieren Sie Ihr Reiseziel, Hotel oder Tourismusangebot mit fesselnden KI-generierten Videos, die Emotionen wecken und Buchungen steigern. Nutzen Sie unsere innovative Videomarketinglösung speziell für die Tourismusindustrie.',
      },
      en: {
        title: city ? `AI Video Marketing for Tourism in ${city}` : 'AI Video Marketing for Tourism',
        subtitle: 'Inspire potential guests with engaging AI videos for hotels, destinations, and travel providers',
        description: 'Present your destination, hotel, or tourism offering with captivating AI-generated videos that evoke emotions and increase bookings. Use our innovative video marketing solution specifically for the tourism industry.',
      },
      fr: {
        title: city ? `Marketing Vidéo IA pour Tourisme à ${city}` : 'Marketing Vidéo IA pour Tourisme',
        subtitle: 'Inspirez les clients potentiels avec des vidéos IA engageantes pour hôtels, destinations et prestataires de voyages',
        description: 'Présentez votre destination, hôtel ou offre touristique avec des vidéos captivantes générées par IA qui suscitent des émotions et augmentent les réservations. Utilisez notre solution de marketing vidéo innovante spécifiquement pour l\'industrie du tourisme.',
      },
      it: {
        title: city ? `Marketing Video AI per Turismo a ${city}` : 'Marketing Video AI per Turismo',
        subtitle: 'Ispira potenziali ospiti con video AI coinvolgenti per hotel, destinazioni e fornitori di viaggi',
        description: 'Presenta la tua destinazione, hotel o offerta turistica con video coinvolgenti generati da AI che suscitano emozioni e aumentano le prenotazioni. Utilizza la nostra innovativa soluzione di marketing video specificamente per l\'industria del turismo.',
      }
    };
    
    return content[currentLanguage as keyof typeof content] || content.en;
  };
  
  const heroContent = getHeroContent();
  
  return (
    <div className="flex flex-col min-h-screen bg-[#121212]">
      <Header />
      <PageTransition>
        <main className="flex-grow pt-24">
          <HeroSection 
            title={heroContent.title} 
            subtitle={heroContent.subtitle} 
            description={heroContent.description}
            industry="tourism"
          />
          
          <WhyKIVideos industry="tourism" />
          
          <ProcessSteps industry="tourism" />
          
          {/* Tourism-Specific Solutions section */}
          <section className="py-16 px-4 md:px-8 bg-gradient-to-br from-purple-900/20 to-black">
            <div className="container mx-auto max-w-6xl">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white text-center">
                {currentLanguage === 'de' ? 'Tourismus-spezifische Lösungen' :
                 currentLanguage === 'en' ? 'Tourism-Specific Solutions' :
                 currentLanguage === 'fr' ? 'Solutions Spécifiques au Tourisme' :
                 'Soluzioni Specifiche per il Turismo'}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
                <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:border-purple-500/50 transition-all">
                  <h3 className="text-xl font-semibold text-white mb-4">
                    {currentLanguage === 'de' ? 'Destinations-Showcase' :
                     currentLanguage === 'en' ? 'Destination Showcases' :
                     currentLanguage === 'fr' ? 'Vitrines de Destinations' :
                     'Vetrine di Destinazioni'}
                  </h3>
                  <p className="text-gray-300">
                    {currentLanguage === 'de' ? 'Präsentieren Sie Reiseziele mit atmospärischen Videos, die die Einzigartigkeit Ihrer Destination hervorheben.' :
                     currentLanguage === 'en' ? 'Present destinations with atmospheric videos that highlight the uniqueness of your location.' :
                     currentLanguage === 'fr' ? 'Présentez des destinations avec des vidéos atmosphériques qui soulignent l\'unicité de votre lieu.' :
                     'Presenta destinazioni con video atmosferici che evidenziano l\'unicità della tua località.'}
                  </p>
                </div>
                
                <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:border-purple-500/50 transition-all">
                  <h3 className="text-xl font-semibold text-white mb-4">
                    {currentLanguage === 'de' ? 'Hotel & Resort Videos' :
                     currentLanguage === 'en' ? 'Hotel & Resort Videos' :
                     currentLanguage === 'fr' ? 'Vidéos d\'Hôtels & Resorts' :
                     'Video di Hotel e Resort'}
                  </h3>
                  <p className="text-gray-300">
                    {currentLanguage === 'de' ? 'Zeigen Sie Unterkunftsmöglichkeiten, Annehmlichkeiten und Erlebnisse mit ansprechenden visuellen Inhalten.' :
                     currentLanguage === 'en' ? 'Showcase accommodations, amenities, and experiences with appealing visual content.' :
                     currentLanguage === 'fr' ? 'Présentez les hébergements, les équipements et les expériences avec un contenu visuel attrayant.' :
                     'Mostra alloggi, servizi ed esperienze con contenuti visivi accattivanti.'}
                  </p>
                </div>
                
                <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:border-purple-500/50 transition-all">
                  <h3 className="text-xl font-semibold text-white mb-4">
                    {currentLanguage === 'de' ? 'Saisonale Kampagnen' :
                     currentLanguage === 'en' ? 'Seasonal Campaigns' :
                     currentLanguage === 'fr' ? 'Campagnes Saisonnières' :
                     'Campagne Stagionali'}
                  </h3>
                  <p className="text-gray-300">
                    {currentLanguage === 'de' ? 'Erstellen Sie zeitlich abgestimmte Videokampagnen für verschiedene Jahreszeiten und spezielle Angebote.' :
                     currentLanguage === 'en' ? 'Create timely video campaigns for different seasons and special offers.' :
                     currentLanguage === 'fr' ? 'Créez des campagnes vidéo opportunes pour différentes saisons et offres spéciales.' :
                     'Crea campagne video tempestive per diverse stagioni e offerte speciali.'}
                  </p>
                </div>
              </div>
            </div>
          </section>
          
          <ExampleGallery industry="tourism" />
          
          <FAQSection industry="tourism" />
          
          <CtaSection industry="tourism" />
        </main>
      </PageTransition>
      <Footer />
      <FloatingCTA />
    </div>
  );
};

export default TourismIndustry;
